import { useEffect } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Label,
  Popconfirm,
  RadioGroup,
  RadioGroupItem,
  Stack,
  Switch,
  toast,
  Typography,
} from "@altimateai/lego";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { isOwner, isAdmin } from "@/services/auth";
import { useMutation } from "@tanstack/react-query";
import {
  upsertCostAttributionConfig,
  UpsertCostAttributionRequest,
} from "@/helpers/costAttributionApi";
import { useCostAttribution } from "@/pages/settings/components/userGroups/useCostAttribution";
import { useBreadcrumb } from "@/components/breadcrumbs";

// Attribution Method Constants
export const ATTRIBUTION_METHOD = {
  WAREHOUSE: "warehouse",
  QUERY: "query",
} as const;

export type AttributionMethod = (typeof ATTRIBUTION_METHOD)[keyof typeof ATTRIBUTION_METHOD];

const costAttributionSchema = z.object({
  enableCostAttribution: z.boolean(),
  attributionMethod: z.enum([ATTRIBUTION_METHOD.WAREHOUSE, ATTRIBUTION_METHOD.QUERY]),
});

type CostAttributionFormData = z.infer<typeof costAttributionSchema>;

const CostAttribution = () => {
  useBreadcrumb("Cost Attribution", {
    defaultParents: [{ label: "Settings", pathname: "/settings", noLink: true }],
  });
  const hasPermissions = isOwner() || isAdmin();
  const { costAttribution: configData, refetch } = useCostAttribution();

  const { watch, reset, getValues, register } = useForm<CostAttributionFormData>({
    resolver: zodResolver(costAttributionSchema),
    defaultValues: {
      enableCostAttribution: false,
      attributionMethod: ATTRIBUTION_METHOD.WAREHOUSE,
    },
  });

  // Update form when data loads
  useEffect(() => {
    if (configData?.enabled !== undefined && configData?.mode !== undefined) {
      reset(
        {
          enableCostAttribution: configData.enabled,
          attributionMethod: configData.mode as AttributionMethod,
        },
        { keepDefaultValues: false }
      );
    }
  }, [configData?.enabled, configData?.mode, reset]);

  // Mutation for updating configuration
  const { mutate: updateConfig, isPending: isUpdating } = useMutation({
    mutationFn: upsertCostAttributionConfig,
    onSuccess: () => {
      toast({ description: "Cost Attribution settings saved successfully" });
      refetch();
    },
    onError: () => {
      toast({
        description: "Failed to save Cost Attribution settings",
        variant: "destructive",
      });
    },
  });

  const enableCostAttribution = watch("enableCostAttribution");
  const attributionMethod = watch("attributionMethod");

  const handleSaveConfirm = () => {
    if (!hasPermissions) return;

    // Get current form values at the time of submission
    const formValues = getValues();
    const requestData: UpsertCostAttributionRequest = {
      enabled: formValues.enableCostAttribution,
      mode: formValues.attributionMethod,
    };

    updateConfig(requestData);
  };

  // Use register for form state management
  const enableCostAttributionField = register("enableCostAttribution");
  const attributionMethodField = register("attributionMethod");

  return (
    <Stack direction="column" gap="lg" className="al-max-w-3xl">
      <div>
        <Typography variant="page-heading" className="al-mb-4">
          Cost Attribution
        </Typography>
        <Typography variant="body" className="al-text-muted-foreground">
          Configure how costs are attributed to teams and users in your organization. This helps
          track resource usage and manage budgets effectively.
        </Typography>
      </div>

      {!hasPermissions && (
        <Alert>
          <AlertDescription>
            You don&apos;t have permission to modify cost attribution settings. Contact your
            administrator for access.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="al-p-6">
          <form>
            <Stack direction="column" gap="lg">
              {/* Enable Cost Attribution Toggle */}
              <Stack direction="row" justify="between" align="center" className="al-py-4">
                <div>
                  <Stack direction="row" align="center" gap="4xl">
                    <Typography variant="subheading" className="al-mb-1">
                      Enable Cost Attribution
                    </Typography>
                    <Switch
                      checked={enableCostAttribution}
                      onCheckedChange={(checked: boolean) => {
                        if (!hasPermissions) return;
                        enableCostAttributionField.onChange({
                          target: { name: enableCostAttributionField.name, value: checked },
                        });
                      }}
                      disabled={!hasPermissions}
                      aria-label="Enable Cost Attribution"
                      ref={enableCostAttributionField.ref}
                    />
                  </Stack>
                  <Typography variant="body" className="al-text-muted-foreground">
                    If enabled, Entity costs will be recorded and attributed to teams.
                  </Typography>
                </div>
              </Stack>

              <div className="al-border-t al-border-gray-200" />

              {/* Configuration Section */}
              <div>
                <Typography variant="section-heading" className="al-mb-4">
                  Configuration
                </Typography>

                <RadioGroup
                  value={attributionMethod}
                  onValueChange={(value: string) => {
                    attributionMethodField.onChange({
                      target: { name: attributionMethodField.name, value },
                    });
                  }}
                  disabled={!hasPermissions || !enableCostAttribution}
                  className="al-space-y-3"
                >
                  {[
                    {
                      value: ATTRIBUTION_METHOD.WAREHOUSE,
                      id: "warehouse",
                      label: "Attribution by Warehouse",
                      description:
                        "Warehouse ownership rules are applied in attributing cost to the teams",
                    },
                    {
                      value: ATTRIBUTION_METHOD.QUERY,
                      id: "query",
                      label: "Attribution by Query",
                      description:
                        "Query ownership rules are applied in attributing cost to the teams",
                    },
                  ].map(({ value, id, label, description }) => (
                    <Card
                      key={id}
                      className={`al-p-4 al-border al-transition-colors ${
                        attributionMethod === value
                          ? "al-border-blue-500 al-bg-blue-50"
                          : "al-border-gray-200"
                      } ${!hasPermissions || !enableCostAttribution ? "al-opacity-50 al-cursor-not-allowed" : ""}`}
                    >
                      <Stack direction="row" align="start" gap="sm">
                        <RadioGroupItem
                          value={value}
                          id={id}
                          className="al-mt-1"
                          disabled={!hasPermissions || !enableCostAttribution}
                        />
                        <div className="al-flex-1">
                          <Label
                            htmlFor={id}
                            className={`al-font-medium ${hasPermissions && enableCostAttribution ? "al-cursor-pointer" : "al-cursor-not-allowed"}`}
                          >
                            {label}
                          </Label>
                          <Typography variant="body" className="al-text-muted-foreground al-mt-1">
                            {description}
                          </Typography>
                        </div>
                      </Stack>
                    </Card>
                  ))}
                </RadioGroup>
              </div>

              {/* Save Button with Confirmation */}
              <Stack justify="end" className="al-pt-4">
                <Popconfirm
                  title="Save Cost Attribution Settings"
                  description={`Are you sure you want to save these cost attribution settings? ${
                    enableCostAttribution
                      ? `Cost attribution will be enabled using ${attributionMethod} ownership rules.`
                      : "Cost attribution will be disabled."
                  }`}
                  onConfirm={handleSaveConfirm}
                  okText="Yes, Save"
                  cancelText="Cancel"
                >
                  <Button type="button" variant="default" disabled={!hasPermissions || isUpdating}>
                    {isUpdating ? "Saving..." : "Save"}
                  </Button>
                </Popconfirm>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default CostAttribution;
